
import { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { getServiceIcon } from "@/utils/imageUtils";

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallbackSrc?: string;
  alt: string;
  serviceType?: string;
}

export const ImageWithFallback = ({ 
  src, 
  fallbackSrc = "/placeholder.svg",
  alt,
  className,
  serviceType,
  ...props 
}: ImageWithFallbackProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const IconComponent = serviceType ? getServiceIcon(serviceType) : null;

  // Add useEffect to preload and validate images
  useEffect(() => {
    if (!src) {
      setError(true);
      setIsLoading(false);
      return;
    }

    // For SVGs, handle differently due to potential CORS issues
    if (src.toLowerCase().endsWith('.svg')) {
      const preloadImage = new Image();
      
      const checkSvgContent = async () => {
        try {
          // Use no-cors mode for external SVGs that might have CORS restrictions
          const isExternalSvg = src.startsWith('http') && !src.includes(window.location.hostname);
          
          if (isExternalSvg) {
            // For external SVGs that might have CORS issues, we'll just try to load the image
            // and rely on the onerror handler if it fails
            console.log('Loading external SVG with potential CORS issues:', src);
          } else {
            // For SVGs we control, we can fetch and validate content
            const response = await fetch(src);
            if (!response.ok) throw new Error('Failed to fetch SVG');
            
            const svgText = await response.text();
            // Check if it's valid SVG content
            if (!svgText.includes('<svg')) {
              console.warn('Invalid SVG content:', src);
              setError(true);
            }
          }
        } catch (err) {
          console.warn('Error loading SVG:', src, err);
          setError(true);
        }
        setIsLoading(false);
      };

      preloadImage.onload = () => {
        if (!preloadImage.width || !preloadImage.height || preloadImage.width < 10) {
          console.warn('SVG dimensions invalid:', src);
          setError(true);
          setIsLoading(false);
        } else {
          // Even if dimensions are valid, check SVG content
          checkSvgContent();
        }
      };

      preloadImage.onerror = () => {
        console.warn('SVG failed to preload:', src);
        setError(true);
        setIsLoading(false);
      };

      // Set crossOrigin to anonymous for external resources
      if (src.startsWith('http') && !src.includes(window.location.hostname)) {
        preloadImage.crossOrigin = "anonymous";
      }

      preloadImage.src = src;
    } else {
      // For non-SVG images, simply preload
      const preloadImage = new Image();
      
      preloadImage.onload = () => {
        if (!preloadImage.width || !preloadImage.height) {
          console.warn('Image dimensions invalid:', src);
          setError(true);
        }
        setIsLoading(false);
      };
      
      preloadImage.onerror = () => {
        console.warn('Image failed to preload:', src);
        setError(true);
        setIsLoading(false);
      };
      
      // Set crossOrigin to anonymous for external resources
      if (src.startsWith('http') && !src.includes(window.location.hostname)) {
        preloadImage.crossOrigin = "anonymous";
      }
      
      preloadImage.src = src;
    }
  }, [src]);

  // If there's an error and we have both a service type and icon component
  if (error && serviceType && IconComponent) {
    return (
      <div className="flex flex-col items-center justify-center p-4">
        <IconComponent className="w-16 h-16 mb-2 text-gray-700" />
        <span className="text-sm font-medium text-gray-700">{serviceType}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <Skeleton className={`absolute inset-0 ${className}`} />
      )}
      <img
        src={error ? fallbackSrc : src}
        alt={alt}
        className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        loading="lazy"
        onError={() => {
          console.warn('Image failed to load:', src);
          setError(true);
          setIsLoading(false);
        }}
        {...props}
      />
    </div>
  );
};
